import * as React from 'react';

import { EventBus, getFirebaseAuth } from '@/shared/utils';
import { SeriesByIdEventKeys, SeriesByIdEvents } from '../events';
import { useAuthStore } from '@/auth';
import { isLeft, isRight } from '@shared-commons/utils';
import { useSeriesByIdStore } from '../store/series-by-id-store';
import { getSeriesByIdSummary } from '../services/summary-details/get-summary-details';
import { getCustomerSubscription } from '../services/customer/get-customer-subscription';
import { getSeriesById } from '../services/series/get-series';
import { LessonProgresses } from '../types/lessons';
import { updateReadUnreadLessonById } from '../services/lesson-progresses/update-lesson-progresses';
import { createReadUnreadLessonById } from '../services/lesson-progresses/create-lesson-progresses';
import { Auth } from 'firebase/auth';
import { getSeriesFavourite } from '../services/favourite/get-favourite';
import { createFavouriteSeries } from '../services/favourite/create-favourite';
import { deleteSeriesFavourite } from '../services/favourite/delete-favourite';

const useSeriesById = (
  auth: Auth | null,
  eventBus: EventBus<SeriesByIdEvents>,
  accessTokenApp?: string,
) => {
  const {
    customerSubscription,
    setSummaryDetails,
    setSeries,
    setCustomerSubscription,
    updateLessonProgress,
    createLessonProgress,
    setLessonProgress,
    setFavouriteSeries,
  } = useSeriesByIdStore();

  const { user } = useAuthStore();

  // CUSTOMER SUBSCRIPTION
  const fetchCustomerSubscription = React.useCallback(
    async ({ email }: { email: string }) => {
      if (!auth) return;
      setCustomerSubscription({ status: { isError: false, isLoading: true } });
      const accessToken = accessTokenApp || (await auth.currentUser!.getIdToken(true));
      const result = await getCustomerSubscription(email, accessToken)();
      if (isRight(result)) {
        setCustomerSubscription({
          data: result?.value || null,
          error: null,
          status: { isError: false, isLoading: false },
        });
      } else if (isLeft(result)) {
        const error = new Error(result.value?.message || 'An error has occurred');
        setCustomerSubscription({ error: error, status: { isError: true, isLoading: false } });
      }
    },
    [auth, user?.email, setCustomerSubscription],
  );

  const fetchSeriesByIdSummary = React.useCallback(
    async ({ seriesDocumentId }: { seriesDocumentId: string }) => {
      if (!auth) return;
      setSummaryDetails({ status: { isError: false, isLoading: true } });
      const accessToken = accessTokenApp || (await auth.currentUser!.getIdToken(true));
      const result = await getSeriesByIdSummary(seriesDocumentId, accessToken, user?.email)();
      if (isRight(result)) {
        setSummaryDetails({
          data: result?.value || null,
          error: null,
          status: { isError: false, isLoading: false },
        });
      } else if (isLeft(result)) {
        const error = new Error(result.value?.message || 'An error has occurred');
        setSummaryDetails({ error: error, status: { isError: true, isLoading: false } });
      }
    },
    [user?.email, auth, setSummaryDetails],
  );

  const fetchSeriesById = React.useCallback(
    async ({ seriesDocumentId }: { seriesDocumentId: string }) => {
      if (!auth) return;
      setSeries({ status: { isError: false, isLoading: true } });
      const accessToken = accessTokenApp || (await auth.currentUser!.getIdToken(true));
      const result = await getSeriesById(user?.email, seriesDocumentId, accessToken)();
      if (isRight(result)) {
        setSeries({
          data: result?.value || null,
          error: null,
          status: { isError: false, isLoading: false },
        });
      } else if (isLeft(result)) {
        const error = new Error(result.value?.message || 'An error has occurred');
        setSeries({ error: error, status: { isError: true, isLoading: false } });
      }
    },
    [auth, user?.email, setSeries],
  );

  // CREATE OR UPDATE READ/UNREAD LESSON

  const createLessonProgresses = React.useCallback(
    async (payload: LessonProgresses & { seriesDocumentId?: string }) => {
      if (!auth) return;
      setLessonProgress({ isError: false, isLoading: true });
      try {
        const accessToken = accessTokenApp || (await auth.currentUser!.getIdToken(true));
        const result = await createReadUnreadLessonById(
          { ...payload, email: user?.email },
          accessToken,
        )();
        if (isRight(result)) {
          createLessonProgress({ ...result.value, lessonDocumentId: payload.documentId });
          setLessonProgress({ isError: false, isLoading: false });
          eventBus.emit(SeriesByIdEventKeys.modals.lessonCompletionModal, null);
        } else {
          throw new Error(result.value?.message || 'An error has occurred');
        }
      } catch (error) {
        setLessonProgress({ isError: true, isLoading: false });
      }
    },
    [user?.email, auth, createLessonProgress, setLessonProgress, eventBus],
  );

  const updateLessonProgresses = React.useCallback(
    async (payload: LessonProgresses) => {
      if (!auth) return;
      setLessonProgress({ isError: false, isLoading: true });
      const accessToken = accessTokenApp || (await auth.currentUser!.getIdToken(true));
      const result = await updateReadUnreadLessonById(payload, accessToken)();
      if (isRight(result)) {
        updateLessonProgress(result?.value);
        setLessonProgress({ isError: false, isLoading: false });
        eventBus.emit(SeriesByIdEventKeys.modals.lessonCompletionModal, null);
      } else if (isLeft(result)) {
        const error = new Error(result.value?.message || 'An error has occurred');
        console.log('error occurred', error);
        setLessonProgress({ isError: true, isLoading: false });
      }
    },
    [auth, updateLessonProgress, setLessonProgress, eventBus],
  );

  // VIEW FAVOURITE
  const fetchFavourite = React.useCallback(
    async ({ seriesDocumentId }: { seriesDocumentId: string }) => {
      if (!auth) return;
      setFavouriteSeries({ isLoading: true });
      const accessToken = await auth.currentUser!.getIdToken(true);
      const result = await getSeriesFavourite(user?.email, seriesDocumentId, accessToken)();
      if (isRight(result)) {
        setFavouriteSeries({
          isLoading: false,
          isFavourite: result.value.isFavourite,
          documentId: result.value.documentId,
        });
      } else if (isLeft(result)) {
        const error = new Error(result.value?.message || 'An error has occurred');
        console.log('error occurred', error);
        setFavouriteSeries({ isLoading: false, isFavourite: false });
      }
    },
    [auth, user?.email, setFavouriteSeries],
  );

  // CREATE FAVOURITE
  const createFavourite = React.useCallback(
    async ({ seriesDocumentId }: { seriesDocumentId: string }) => {
      if (!auth) return;
      setFavouriteSeries({ isLoading: true });
      const accessToken = await auth.currentUser!.getIdToken(true);
      const result = await createFavouriteSeries(user?.email, seriesDocumentId, accessToken)();
      if (isRight(result)) {
        setFavouriteSeries({
          isLoading: false,
          isFavourite: result.value.isFavourite,
          documentId: result.value.documentId,
        });
      } else if (isLeft(result)) {
        const error = new Error(result.value?.message || 'An error has occurred');
        console.log('error occurred', error);
        setFavouriteSeries({ isLoading: false });
      }
    },
    [auth, user?.email, setFavouriteSeries],
  );

  // DELETE FAVOURITE
  const deleteFavourite = React.useCallback(
    async ({ favouriteDocumentId }: { favouriteDocumentId: string }) => {
      if (!auth) return;
      setFavouriteSeries({ isLoading: true });
      const accessToken = await auth.currentUser!.getIdToken(true);
      const result = await deleteSeriesFavourite(favouriteDocumentId, accessToken)();
      if (isRight(result)) {
        setFavouriteSeries({ isLoading: false, isFavourite: false, documentId: null });
      } else if (isLeft(result)) {
        const error = new Error(result.value?.message || 'An error has occurred');
        console.log('error occurred', error);
        setFavouriteSeries({ isLoading: false });
      }
    },
    [auth, setFavouriteSeries],
  );

  return {
    fetchSeriesById,
    updateLessonProgresses,
    fetchCustomerSubscription,
    customerSubscription,
    createLessonProgresses,
    fetchSeriesByIdSummary,
    fetchFavourite,
    createFavourite,
    deleteFavourite,
  };
};

export { useSeriesById };
